import { createRouter, createWebHistory } from 'vue-router';
import Login from '@/views/auth/Login.vue';
import { setupRouterWithAuth } from './setupRouterWithAuth';
import { useAdminInfo } from '@/stores/useAdminInfo';
import {
    checkRoutePermission,
    requiresRoutePermissionCheck,
    ADMIN_NO_PERMISSION_ROUTE
} from './permissions';

import Layout from '@/layouts/Layout.vue';
import Dashboard from '@/views/Dashboard.vue';
import { DashboardOutlined } from '@ant-design/icons-vue';
import {
    membersTopLevelRoutes,
    membersMgmtFolderRoute,
    membersMgmtChildrenRoutes
} from './routes/members';
import { pointsFolderRoute, pointsChildrenRoutes } from './routes/points';
import { levelsFolderRoute, levelsChildrenRoutes } from './routes/levels';
import { missionsFolderRoute, missionsChildrenRoutes } from './routes/missions';
import { quizzesFolderRoute, quizzesChildrenRoutes } from './routes/quizzes';
import { productsFolderRoute, productsChildrenRoutes, orderDeskFolderRoute, orderDeskChildrenRoutes, orderDeskReadonlyLegacyRoutes } from './routes/products';
import {
    inventoryFolderRoute,
    inventoryChildrenRoutes,
    inventoryLegacyRedirects
} from './routes/inventory';
import {
    formsFolderRoute,
    formsChildrenRoutes,
    formsLegacyRoutes
} from './routes/forms';
import { iceFireFolderRoute, iceFireChildrenRoutes } from './routes/iceFire';
import {
    partnerOpsFolderRoute,
    partnerOpsChildrenRoutes,
    partnerOpsLegacyRoutes
} from './routes/partnerOps';
import { policyRoute } from './routes/policy';
import { platformLegacyRedirects } from './routes/platform';
import { auditLogRoute } from './routes/audit';

const publicRoutes = [
    {
        path: '/login',
        name: 'Login',
        component: Login
    },
    {
        path: '/sso-callback',
        name: 'SsoCallback',
        component: () => import('@/views/auth/SsoCallback.vue'),
        meta: { title: 'SSO 登入', public: true }
    },
    {
        path: '/',
        component: Layout,
        redirect: '/dashboard',
        children: [
            {
                path: '/dashboard',
                component: Dashboard,
                name: 'Dashboard',
                meta: {
                    title: '控制台',
                    icon: DashboardOutlined
                }
            },
            {
                path: '/no-permission',
                component: () => import('@/views/error/NoPermission.vue'),
                name: ADMIN_NO_PERMISSION_ROUTE,
                meta: { title: '無權限', hidden: true }
            },
            ...membersTopLevelRoutes,
            {
                ...partnerOpsFolderRoute,
                children: partnerOpsChildrenRoutes
            },
            ...partnerOpsLegacyRoutes,
            {
                ...pointsFolderRoute,
                children: pointsChildrenRoutes
            },
            {
                ...levelsFolderRoute,
                children: levelsChildrenRoutes
            },
            {
                ...missionsFolderRoute,
                children: missionsChildrenRoutes
            },
            {
                ...quizzesFolderRoute,
                children: quizzesChildrenRoutes
            },
            {
                ...productsFolderRoute,
                children: productsChildrenRoutes
            },
            {
                ...orderDeskFolderRoute,
                children: orderDeskChildrenRoutes
            },
            ...orderDeskReadonlyLegacyRoutes,
            {
                ...inventoryFolderRoute,
                children: inventoryChildrenRoutes
            },
            {
                ...formsFolderRoute,
                children: formsChildrenRoutes
            },
            // 舊路徑 redirect 掛在 Layout 同層（不要塞進 folder，以免干擾側欄 children）
            ...formsLegacyRoutes,
            ...inventoryLegacyRedirects,
            {
                ...iceFireFolderRoute,
                children: iceFireChildrenRoutes
            },
            policyRoute,
            // 操作紀錄 → 系統營運（側欄置底）
            auditLogRoute,
            {
                ...membersMgmtFolderRoute,
                children: membersMgmtChildrenRoutes
            },
            ...platformLegacyRedirects
        ]
    },
    {
        path: '/:pathMatch(.*)*',
        component: () => import('@/views/error/404.vue')
    }
];

export const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: publicRoutes
});

const CHUNK_RELOAD_KEY = 'admin_fe_chunk_reload';

// 部署後舊 index 引用已刪除的 lazy chunk → 自動重整一次
router.onError((error, to) => {
    const message = String(error?.message ?? error ?? '');
    const isChunkError =
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('Loading chunk');

    if (!isChunkError) {
        return;
    }

    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) !== '1') {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        window.location.assign(to.fullPath);
        return;
    }

    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
});

const isPermissionGuardedRoute = requiresRoutePermissionCheck;

router.beforeEach(async (to, from, next) => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);

    const accessToken = localStorage.getItem('accessToken');
    const adminStore = useAdminInfo();

    if (to.path === '/login' || to.name === 'Login') {
        if (accessToken) {
            // 已登入：離開登入頁
            if (!adminStore.profileFetched) {
                try {
                    const ok = await adminStore.fetchUserInfo();
                    if (!ok) {
                        adminStore.clearAllInfo();
                        next();
                        return;
                    }
                } catch {
                    adminStore.clearAllInfo();
                    next();
                    return;
                }
            }
            const nextPath =
                typeof to.query.next === 'string'
                    ? to.query.next
                    : typeof to.query.redirect === 'string'
                      ? to.query.redirect
                      : '';
            if (
                nextPath.startsWith('/') &&
                !nextPath.startsWith('//') &&
                !nextPath.includes('#')
            ) {
                next({ path: nextPath, replace: true });
                return;
            }
            next({ name: 'Dashboard', replace: true });
            return;
        }
        next();
        return;
    }

    if (to.path === '/sso-callback' || to.name === 'SsoCallback') {
        next();
        return;
    }

    if (!accessToken) {
        next({
            name: 'Login',
            query: { next: to.fullPath, redirect: to.fullPath }
        });
        return;
    }

    // 每次新 session 必打 /me，避免 localStorage 舊權限名單漏掉表單／背包
    if (!adminStore.profileFetched) {
        try {
            const success = await adminStore.fetchUserInfo();
            if (!success) {
                adminStore.clearAllInfo();
                next({ name: 'Login' });
                return;
            }
        } catch (error) {
            console.error('Failed to fetch user info:', error);
            adminStore.clearAllInfo();
            next({ name: 'Login' });
            return;
        }
    }

    const permOptions = { roles: adminStore.roles || [] };
    if (
        isPermissionGuardedRoute(to.name) &&
        !checkRoutePermission(to.name, adminStore.permissions || [], permOptions)
    ) {
        next({ name: ADMIN_NO_PERMISSION_ROUTE });
        return;
    }

    next();
});

export default router;
